import { useMemo } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebounce } from '@/hooks/useDebounce';
import { apiPost, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { getActiveTenantQueryKey } from '@/lib/activeTenant';
import type { EditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';

// NOT-306 (spec §8) — evaluate which upsell candidates apply to the items
// currently on the estimate, given its live context.

export type UpsellRelationshipType = 'upsell' | 'accessory' | 'alternative';

export interface UpsellSuggestedItem {
  id: string;
  name: string;
  code: string;
  type: string;
  unit: string;
  description: string;
  basePrice: number;
  laborRate: number;
  costBasis: number;
  markupPercent: number;
  priceStrategy: string;
  taxable: boolean;
  taxLineType: string | null;
  currency: string;
  itemVersion: number;
  displayConfig: unknown;
}

export interface EstimateUpsellSource {
  lineItemId: string;
  /** This source's own auto flag — auto vs recommend differs per source. */
  auto: boolean;
  /** This source's own copy/type — shown/persisted per the source the user added. */
  reason: string | null;
  relationshipType: UpsellRelationshipType;
}

export interface EstimateUpsell {
  /** Every source line item that produced this candidate, each with its own copy. */
  sources: EstimateUpsellSource[];
  aiWeight: number;
  /** Winner's copy/type — display fallback. */
  reason: string | null;
  relationshipType: UpsellRelationshipType;
  suggestedItem: UpsellSuggestedItem;
}

interface EvaluateUpsellsResponse {
  eligible: EstimateUpsell[];
}

export interface UseEstimateUpsellsArgs {
  lineItems: EditableLineItem[];
  estimateTotal: number;
  membershipStatus: string | null;
  asset?: { ageYears: number | null; category: string | null } | null;
  /**
   * Gate the evaluation until upstream context (e.g. the membership lookup) has
   * settled — otherwise an in-flight "no membership yet" would be evaluated as a
   * confirmed non-member and could auto-insert non-member upsells (NOT-306).
   */
  enabled?: boolean;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uniqueCatalogIds(ids: string[]): string[] {
  return [...new Set(ids.filter((id) => UUID_REGEX.test(id)))];
}

export function useEstimateUpsells({
  lineItems,
  estimateTotal,
  membershipStatus,
  asset = null,
  enabled = true,
}: UseEstimateUpsellsArgs) {
  // Source ids drive the evaluation. Exclude lines that are themselves upsells
  // so adding an upsell never recursively suggests its own upsells (anti-loop).
  const sourceIds = useMemo(
    () => uniqueCatalogIds(lineItems.filter((li) => !li.isUpsell).map((li) => li.lineItemId)),
    [lineItems],
  );

  // Every catalog item present (incl. upsell lines) — used for requiresItems and
  // to exclude already-added items from the suggestions.
  const presentIds = useMemo(
    () => uniqueCatalogIds(lineItems.map((li) => li.lineItemId)),
    [lineItems],
  );

  const debouncedTotal = useDebounce(estimateTotal, 400);
  const debouncedSourceKey = useDebounce(sourceIds.join(','), 400);
  const debouncedPresentKey = useDebounce(presentIds.join(','), 400);

  const query = useApiQuery<EvaluateUpsellsResponse>({
    queryKey: [
      'estimateUpsells',
      getActiveTenantQueryKey(),
      debouncedSourceKey,
      debouncedPresentKey,
      debouncedTotal,
      membershipStatus,
      asset?.ageYears ?? null,
      asset?.category ?? null,
    ],
    enabled: enabled && sourceIds.length > 0,
    showErrorToast: false,
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      apiPost<EvaluateUpsellsResponse>(
        operationsClient,
        opsEstimateEndpoints.evaluateUpsells(),
        {
          lineItemIds: sourceIds,
          context: {
            estimateTotal: debouncedTotal,
            customerMembershipStatus: membershipStatus,
            asset,
            estimateItemIds: presentIds,
          },
        },
        { signal },
      ),
  });

  const presentSet = useMemo(() => new Set(presentIds), [presentIds]);

  // All eligible candidates (present-filtered). Auto-vs-recommend is decided in
  // the form, which knows which source the user added — `auto` is per-source.
  const upsells = useMemo(() => {
    // `enabled: false` only stops refetching — `query.data` lingers. Whenever the
    // evaluation context isn't ready (no sources, or the customer/membership
    // lookup hasn't settled / is switching), the previous result must not survive
    // — short-circuit to empty so stale recommendations never stay clickable and
    // stale auto-upsells can't be re-applied (NOT-306).
    if (!enabled || sourceIds.length === 0) return [] as EstimateUpsell[];
    return (query.data?.eligible ?? []).filter(
      // Defensive: never recommend an item already on the estimate.
      (u) => !presentSet.has(u.suggestedItem.id),
    );
  }, [query.data, presentSet, sourceIds.length, enabled]);

  return {
    upsells,
    isLoading: query.isLoading && enabled && sourceIds.length > 0,
  };
}
